import { Fragment, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PrinterIcon } from '@heroicons/react/24/outline';
import CalendarPreviewView from './CalendarPreviewView';
import { track } from '../lib/track';
import { useTheme } from '../lib/theme/index.js';

interface CalendarPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: Array<{
    id: string;
    name: string;
    camp_schedules?: Array<{
      id: string;
      camp_id: string;
      child_id?: string;
      start_date: string;
      end_date: string;
      event_year?: number | null;
      camp?: {
        id: string;
        name: string;
        description?: string;
        address?: string;
        city?: string;
        state?: string;
        zip_code?: string;
      };
    }>;
  }>;
  selectedYear: number;
  userId?: string;
}

export default function CalendarPreviewModal({
  isOpen,
  onClose,
  children,
  selectedYear,
  userId
}: CalendarPreviewModalProps) {
  const { currentTheme } = useTheme();
  
  // Track modal opened event (PostHog + dataLayer for GTM/Meta)
  useEffect(() => {
    if (isOpen && userId) {
      track('calendar_preview_opened', {
        persona: 'parent',
        user_id: userId,
        year: selectedYear,
        children_count: children.length
      });
    }
  }, [isOpen, userId, selectedYear, children.length]);

  const handlePrint = () => {
    track('calendar_preview_print_initiated', {
      persona: 'parent',
      user_id: userId,
      year: selectedYear,
      children_count: children.length
    });

    // Trigger native browser print dialog
    window.print();

    // Track print completion (we can't detect if user actually printed, but we can track the action)
    setTimeout(() => {
      track('calendar_preview_print_completed', {
        persona: 'parent',
        user_id: userId,
        year: selectedYear
      });
    }, 100);
  };

  return (
    <>
      {/* Print-specific styles */}
      <style>{`
        @media print {
          /* Force colors to print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Hide page elements but NOT calendar content */
          body > header,
          body > nav,
          body > main:not([data-calendar-print-wrapper]),
          body > footer,
          [role="banner"],
          [role="main"]:not([data-calendar-print-wrapper]),
          [role="contentinfo"],
          .site-header,
          .site-footer {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-before: avoid !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Hide root children except calendar wrapper */
          #root > *:not([data-calendar-print-wrapper]) {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          
          /* Show calendar wrapper and all its children */
          [data-calendar-print-wrapper],
          [data-calendar-print-wrapper] * {
            visibility: visible !important;
          }
          
          /* Reset positioning for print */
          [data-calendar-print-wrapper] {
            position: static !important;
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            page-break-before: always;
          }
          
          /* Dialog container */
          [data-calendar-print-wrapper] [role="dialog"] {
            position: static !important;
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            transform: none !important;
            opacity: 1 !important;
          }
          
          /* Hide backdrop */
          [data-calendar-print-wrapper] [role="dialog"] > div:first-child {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Dialog panel */
          [data-calendar-print] {
            position: static !important;
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            overflow: visible !important;
            box-shadow: none !important;
            border: none !important;
            transform: none !important;
            border-radius: 0 !important;
            opacity: 1 !important;
          }
          
          /* Hide header with buttons */
          [data-calendar-print] > div:first-child {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* Show logo */
          [data-calendar-print] > div:nth-child(2) {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: absolute !important;
            top: 1rem !important;
            right: 1rem !important;
            z-index: 10 !important;
          }
          
          /* Show calendar content container */
          [data-calendar-print] > div:last-child {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            padding: 1rem !important;
            padding-top: 4rem !important;
          }
          
          /* Calendar preview - ensure all children are visible */
          [data-calendar-content] {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: 100% !important;
          }
          
          /* Keep flex and grid layouts */
          [data-calendar-content] .flex {
            display: flex !important;
            visibility: visible !important;
          }
          
          [data-calendar-content] .grid {
            display: grid !important;
            visibility: visible !important;
          }
          
          /* Reset body and html */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            height: auto !important;
          }
        }
      `}</style>

      <div data-calendar-print-wrapper>
        <Transition appear show={isOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25 print:hidden" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto print:static print:overflow-visible print:inset-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center print:p-0 print:items-start print:min-h-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel 
                  data-calendar-print
                  className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all print:shadow-none print:rounded-none print:max-w-none print:transform-none print:overflow-visible print:static print:w-full"
                >
                  {/* Header - Hidden in print, logo will show instead */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 print:hidden">
                    <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                      Calendar Preview
                    </Dialog.Title>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handlePrint}
                        className="btn btn-primary inline-flex items-center gap-2"
                      >
                        <PrinterIcon className="h-5 w-5" />
                        Print Calendar
                      </button>
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 transition-colors"
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-6 w-6" />
                      </button>
                    </div>
                  </div>

                  {/* Logo for Print - Replaces header area */}
                  <div className="hidden print:block print:absolute print:top-4 print:right-4 print:z-10">
                    <img 
                      src={currentTheme.logo} 
                      alt="CampWhiz" 
                      className="h-10"
                    />
                  </div>

                  {/* Preview Content */}
                  <div className="p-6 print:p-4 print:pt-16">
                    <CalendarPreviewView
                      children={children}
                      selectedYear={selectedYear}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      </div>
    </>
  );
}

